// ═══════════════════════════════════════════════════════════════════════════════
// name-utils.ts — Utilitaires de parsing de noms français
// Gère les prénoms composés (Jean Pierre → Jean Pierre / Jean-Pierre)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Liste des prénoms composés français les plus courants.
 * Stockés en minuscule, sans tiret, pour match flexible.
 * Source : état civil français, prénoms les plus fréquents post-1950.
 */
const COMPOUND_FIRST_NAMES: Set<string> = new Set([
  // Jean-*
  "jean pierre", "jean paul", "jean marc", "jean louis", "jean claude",
  "jean michel", "jean francois", "jean françois", "jean luc", "jean jacques",
  "jean marie", "jean philippe", "jean yves", "jean charles", "jean christophe",
  "jean daniel", "jean baptiste", "jean noel", "jean noël", "jean rene",
  "jean rené", "jean bernard", "jean patrick", "jean laurent", "jean denis",
  "jean max", "jean hugues", "jean sebastien", "jean sébastien", "jean eric",
  "jean éric", "jean guy", "jean david", "jean robert", "jean pascal",
  "jean edouard", "jean édouard", "jean gabriel", "jean antoine",
  // Marie-*
  "marie anne", "marie claire", "marie france", "marie claude", "marie therese",
  "marie thérèse", "marie helene", "marie hélène", "marie christine",
  "marie pierre", "marie laure", "marie line", "marie jose", "marie josée",
  "marie jeanne", "marie louise", "marie paule", "marie ange", "marie cecile",
  "marie cécile", "marie agnes", "marie agnès", "marie noelle", "marie noëlle",
  "marie dominique", "marie odile", "marie rose", "marie andree", "marie andrée",
  "marie sophie", "marie charlotte", "marie emilie", "marie émilie",
  // Anne-*
  "anne marie", "anne sophie", "anne laure", "anne charlotte", "anne claire",
  "anne cecile", "anne cécile", "anne lise", "anne gaelle", "anne gaëlle",
  "anne frederique", "anne frédérique",
  // Pierre-*
  "pierre louis", "pierre yves", "pierre antoine", "pierre marie",
  "pierre alexandre", "pierre emmanuel", "pierre henri", "pierre alain",
  "pierre andre", "pierre andré", "pierre olivier", "pierre jean",
  // Charles-*
  "charles henri", "charles edouard", "charles édouard", "charles antoine",
  "charles eric", "charles éric", "charles emmanuel",
  // François(e)-*
  "francois xavier", "françois xavier", "francoise marie", "françoise marie",
  // Louis-*
  "louis philippe", "louis marie", "louis charles",
  // Paul-*
  "paul henri", "paul andre", "paul andré", "paul emile", "paul émile",
  // Autres courants
  "marc antoine", "marc andre", "marc andré", "marc olivier",
  "philippe henri", "philippe marie",
  "patrick jean", "bernard marie",
  "marie france", "marie claude",
  "dominique marie",
  "jacques henri",
  "nicolas marie",
]);

// ─── splitFullName ──────────────────────────────────────────────────────────

export interface SplitNameResult {
  firstName: string;
  lastName: string;
}

/**
 * Sépare un nom complet en { firstName, lastName }.
 * Utilise la liste de prénoms composés pour éviter le split naïf.
 *
 * "Jean Pierre Dupont" → { firstName: "Jean Pierre", lastName: "Dupont" }
 * "Jean Dupont"        → { firstName: "Jean", lastName: "Dupont" }
 * "Dupont"             → { firstName: "Dupont", lastName: "" }
 * ""                   → { firstName: "", lastName: "" }
 */
export function splitFullName(fullName: string): SplitNameResult {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };

  const parts = trimmed.split(/\s+/);

  if (parts.length <= 1) {
    return { firstName: parts[0] ?? "", lastName: "" };
  }

  // Tester si les 2 premiers mots forment un prénom composé connu
  if (parts.length >= 3) {
    const candidateCompound = `${parts[0]} ${parts[1]}`.toLowerCase();
    if (COMPOUND_FIRST_NAMES.has(candidateCompound)) {
      return {
        firstName: `${parts[0]} ${parts[1]}`,
        lastName: parts.slice(2).join(" "),
      };
    }
  }

  // Tester aussi avec tiret (Jean-Pierre Dupont → split sur tiret reconnu)
  if (parts.length >= 2) {
    const firstWord = parts[0]!;
    if (firstWord.includes("-")) {
      const withoutHyphen = firstWord.replace(/-/g, " ").toLowerCase();
      if (COMPOUND_FIRST_NAMES.has(withoutHyphen)) {
        return {
          firstName: firstWord,
          lastName: parts.slice(1).join(" "),
        };
      }
    }
  }

  // Split classique : premier mot = prénom, reste = nom
  return {
    firstName: parts[0]!,
    lastName: parts.slice(1).join(" "),
  };
}

// ─── detectCompoundFirstName ────────────────────────────────────────────────

export interface CompoundDetectionResult {
  suggestion: string | null;
  isCompound: boolean;
}

/**
 * Détecte si un prénom saisi contient un prénom composé connu sans tiret.
 * Retourne une suggestion avec tiret si c'est le cas.
 *
 * "Jean Pierre" → { suggestion: "Jean-Pierre", isCompound: true }
 * "Jean-Pierre" → { suggestion: null, isCompound: false } (déjà avec tiret)
 * "Jean"        → { suggestion: null, isCompound: false }
 */
export function detectCompoundFirstName(
  firstName: string,
): CompoundDetectionResult {
  const trimmed = firstName.trim();
  if (!trimmed) return { suggestion: null, isCompound: false };

  // Déjà un tiret → pas besoin de suggestion
  if (trimmed.includes("-")) return { suggestion: null, isCompound: false };

  // Vérifier si c'est un prénom composé connu (avec espace)
  const lower = trimmed.toLowerCase();
  if (COMPOUND_FIRST_NAMES.has(lower)) {
    // Construire la suggestion avec tiret en préservant la casse originale
    const parts = trimmed.split(/\s+/);
    const suggestion = parts.join("-");
    return { suggestion, isCompound: true };
  }

  return { suggestion: null, isCompound: false };
}
